import { CloseOutlined, DeleteOutlined, EditOutlined, LoadingOutlined } from "@ant-design/icons";
import { Col, Dropdown, MenuProps, Popconfirm, Row, Upload, message } from "antd";
import type { RcFile, UploadProps } from "antd/es/upload/interface";
import { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import EditIcon from "../../../assets/icons/edit-pen.svg";
import EditCover1 from "../../../assets/images/edit-cover-1.webp";
import EditCover2 from "../../../assets/images/edit-cover-2.webp";
import { IUserInfos } from "../../../helpers/types";
import { AppDispatch } from "../../../redux/store";
import { updateToDefaultAvatar } from "../../../redux/users/usersSlice";
import { userService } from "../../../services";
import { convertHeicFile, isImageAcceptedFormat } from "../../../utls/FunctionsUtil";

interface IProps {
  user: IUserInfos | undefined;
  t: TFunction<"translation", undefined, "translation">;
}
export default function EditCreatorAvatar({ user, t }: IProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [loadingAvatar, setLoadingAvatar] = useState<boolean>(false);
  const [openAvatarOptions, setOpenAvatarOptions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isScreenLarge, setIsScreenLarge] = useState(window.innerWidth > 680);

  useEffect(() => {
    const handleResize = () => {
      setIsScreenLarge(window.innerWidth > 680);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenAvatarOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const uploadAvatarImage = useCallback( async (file: File) => {
    const formData = new FormData();
    formData.append("avatar", file);
    userService.updateAvatarImage(formData).then((result) => {
        setLoadingAvatar(false);
        dispatch(updateToDefaultAvatar(result));
        message.success(t("avatar-updated-success-message"));
    }).catch((error) => {
      message.error(t(error));
    });
  },[dispatch, t]);

  const removeAvatarImage = useCallback(async () => {
    userService.updateToDefaultAvatar().then((result) => {
        setOpenAvatarOptions(false);
        dispatch(updateToDefaultAvatar(result));
        message.success(t("avatar-removed-success-message"));
    }).catch((error) => {
      message.error(t(error));
    });
  }, [dispatch, t]);

  const beforeUploadAvatar: UploadProps["beforeUpload"] = useCallback( async (file: RcFile): Promise<File | boolean | string> => {
    setLoadingAvatar(true);
    // If the file is in an accepted format, proceed without conversion
    if (isImageAcceptedFormat(file.type)) {
      uploadAvatarImage(file);
      return false;
    }

    try {
      if (file.type === "image/heic" || file.type === "") {
        // convert file
        const convertedFile = await convertHeicFile(file);
        uploadAvatarImage(convertedFile);
        return false;
      } else {
        message.error(t("unsupported-file-format-error-message"));
        return Upload.LIST_IGNORE;
      }
    } catch (error) {
      message.error(t("unsupported-file-format-error-message"));
      return Upload.LIST_IGNORE;
    }
  }, [t, uploadAvatarImage]);

  const handleAvatarMenuClick: MenuProps["onClick"] = (e) => {
    if (e.key !== "1") {
      setOpenAvatarOptions(false);
    }
  };

  const avatarActions = useMemo(() => [
    {
      label: (
        <Upload
          showUploadList={false}
          beforeUpload={beforeUploadAvatar}
          disabled={loadingAvatar}
          accept={"image/*"}
        >
          <span>{t("edit-avatar")}</span>
        </Upload>
      ),
      key: "0",
      icon: <EditOutlined />,
    },
    {
      label: (
        <Popconfirm
          placement="bottom"
          disabled={user?.avatar?.includes("default.jpg")}
          title={t("delete-avatar")}
          description={t("delete-avatar-confirm-message")}
          onConfirm={removeAvatarImage}
          okText={t("yes")}
          cancelText={t("no")}
        >
          <span>{t("delete-avatar")}</span>
        </Popconfirm>
      ),
      key: "1",
      icon: <DeleteOutlined />,
      disabled: user?.avatar?.includes("default.jpg"),
    },
    {
      label: <span>{t("cancel")}</span>,
      key: "2",
      icon: <CloseOutlined />,
    },
  ], [user, loadingAvatar, t, beforeUploadAvatar, removeAvatarImage]);

  const handleOpenAvatarChange = () => { setOpenAvatarOptions(!openAvatarOptions) };

  return (
    <Row className="w-full justify-content-between mt-20">
      <Col className="vertical-center pl-11">
        <img src={EditCover1} width={90} alt="" />
      </Col>
      <Col className="editProfileAvatarWrapper mt-20">
        <div className="editProfileAvatarUploadContent" ref={!isScreenLarge ? dropdownRef : null}>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: avatarActions,
              onClick: handleAvatarMenuClick,
            }}
            open={openAvatarOptions}
            onOpenChange={(open) => {
              setOpenAvatarOptions(open);
            }}
          >
            <img src={EditIcon} width={28} alt="icon" onClick={handleOpenAvatarChange} />
          </Dropdown>
        </div>
        <div className="editProfileAvatarContent">
          <div
            className="editProfileAvatarImage"
            style={{
              backgroundImage: `url("${user?.avatar}")`,
            }}
          >
            {loadingAvatar ? <LoadingOutlined style={{ fontSize: "32px" }} /> : null}
          </div>
        </div>
      </Col>
      <Col className="vertical-center">
        <img src={EditCover2} width={95} alt="" />
      </Col>
    </Row>
  );
}
